import React, { useState, useMemo } from 'react';

const STABLECOINS = [
  { symbol: 'USDC', name: 'USD Coin', icon: '💵' },
];

const SUPPORTED_NETWORKS = [
  { id: 'base',        name: 'Base',         chainId: 8453,  icon: '🔵' },
  { id: 'baseSepolia', name: 'Base Sepolia', chainId: 84532, icon: '🧪', testnet: true },
];

const BILLING_INTERVALS = [
  { value: 'monthly',   label: 'Monthly',   seconds: 30  * 86400, periodsPerYear: 12 },
  { value: 'quarterly', label: 'Quarterly', seconds: 90  * 86400, periodsPerYear: 4  },
  { value: 'yearly',    label: 'Yearly',    seconds: 365 * 86400, periodsPerYear: 1  },
];

const fmtAmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const inputClass = "w-full bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 placeholder:text-white/30 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-sm transition-all";
const labelClass = "block text-[10px] font-medium text-white/35 uppercase tracking-widest mb-1.5";

function WidgetGenerator({ onClose, merchantAddress, subsAddress, usdcAddress, chainId }) {
  const [formData, setFormData] = useState({
    planName:        'Premium Plan',
    features:        'Unlimited access to all issues\nWeekly newsletter delivered to your inbox\nExclusive subscriber-only content\nFull archive access\nEarly access to new features\nCancel anytime',
    price:           '9.99',
    billingInterval: 'monthly',
    annualDiscount:  '20',
    token:           'USDC',
    network:         SUPPORTED_NETWORKS[0].id,
    renewalReminder: '3',
    walletAddress:   merchantAddress || '',
  });
  const [featuresInput, setFeaturesInput] = useState(formData.features);
  const [copied, setCopied] = useState(false);

  const { iframeSrc, generatedCode, yearlyPrice, hasDiscount } = useMemo(() => {
    const selectedInterval = BILLING_INTERVALS.find(i => i.value === formData.billingInterval) ?? BILLING_INTERVALS[0];
    const intervalSeconds = selectedInterval.seconds;
    const discount        = parseFloat(formData.annualDiscount) || 0;
    const yp              = (parseFloat(formData.price) * selectedInterval.periodsPerYear * (1 - discount / 100)).toFixed(2);
    // Already billing annually — there's nothing to discount up to.
    const disc            = discount > 0 && formData.billingInterval !== 'yearly';

    const featuresPiped = formData.features.split('\n').map(f => f.trim()).filter(Boolean).join('|');

    const config = {
      contract:  subsAddress,
      usdc:      usdcAddress,
      merchant:  formData.walletAddress,
      chain:     chainId,
      plan:      formData.planName,
      features:  featuresPiped,
      price:     formData.price,
      interval:  intervalSeconds,
      discount:  formData.annualDiscount,
      token:     formData.token,
      reminder:  formData.renewalReminder,
    };

    const params = new URLSearchParams({
      contract:  subsAddress,
      usdc:      usdcAddress,
      merchant:  formData.walletAddress,
      chain:     chainId,
      plan:      formData.planName,
      features:  featuresPiped,
      price:     formData.price,
      interval:  intervalSeconds,
      discount:  formData.annualDiscount,
      token:     formData.token,
      reminder:  formData.renewalReminder,
    });
    const src  = `${window.location.origin}${window.location.pathname}#payment?${params.toString()}`;
    const code = `<!-- FlowSub Subscription Widget -->
<div id="crypto-sub-widget" data-config='${JSON.stringify(config)}'></div>
<script src="${window.location.origin}/widget.js"></script>`;

    return { iframeSrc: src, generatedCode: code, yearlyPrice: yp, hasDiscount: disc };
  }, [formData, subsAddress, usdcAddress, chainId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleBtn = (active) =>
    `px-3 py-2.5 rounded-xl border transition-all text-center text-sm font-medium ${
      active
        ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
        : 'border-white/[0.1] hover:border-white/[0.2] text-white/50 hover:text-white/70'
    }`;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { e.currentTarget._mbdown = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.currentTarget._mbdown) onClose(); }}
    >
      <div
        className="bg-[#0d1117] border border-white/[0.08] rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-5 border-b border-white/[0.06] sticky top-0 bg-[#0d1117] z-10 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Create Subscription Widget</h2>
              <p className="text-sm text-white/50 mt-1">Generate embeddable code for your website</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-white/[0.08] rounded-full transition-colors text-white/40 hover:text-white"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-x divide-white/[0.06]">

          {/* Left — Configuration */}
          <div className="px-6 py-5 space-y-5">
            <p className="text-[10px] uppercase tracking-widest text-white/35 font-medium">Configuration</p>

            {/* Plan Name */}
            <div>
              <label className={labelClass}>Plan Name</label>
              <input
                type="text"
                value={formData.planName}
                onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                placeholder="e.g., Premium Plan"
                className={inputClass}
              />
            </div>

            {/* Plan Features */}
            <div>
              <label className={labelClass}>Plan Features</label>
              <textarea
                value={featuresInput}
                onChange={(e) => setFeaturesInput(e.target.value)}
                onBlur={(e) => setFormData(fd => ({ ...fd, features: e.target.value }))}
                placeholder="One feature per line"
                rows={5}
                className={inputClass + " resize-none"}
              />
              <p className="text-xs text-white/30 mt-1.5">One feature per line. Shown on the payment page.</p>
            </div>

            {/* Price */}
            <div>
              <label className={labelClass}>Price</label>
              <div className="relative">
                <span className="absolute left-4 top-2.5 text-white/30 font-medium text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="9.99"
                  className={inputClass + " pl-8"}
                />
              </div>
            </div>

            {/* Billing Interval */}
            <div>
              <label className={labelClass}>Billing Interval</label>
              <div className="grid grid-cols-3 gap-2">
                {BILLING_INTERVALS.map(interval => (
                  <button
                    key={interval.value}
                    onClick={() => setFormData({ ...formData, billingInterval: interval.value })}
                    className={toggleBtn(formData.billingInterval === interval.value)}
                  >
                    {interval.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Annual Discount */}
            <div>
              <label className={labelClass}>Annual Subscription Discount (%)</label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={formData.annualDiscount}
                onChange={(e) => setFormData({ ...formData, annualDiscount: e.target.value })}
                placeholder="20"
                className={inputClass}
              />
              {hasDiscount && (
                <p className="text-xs text-white/40 mt-1.5">
                  Yearly price: <span className="font-semibold text-emerald-400">${fmtAmt(yearlyPrice)}/year</span> (save {formData.annualDiscount}%)
                </p>
              )}
              {!hasDiscount && formData.billingInterval === 'yearly' && (
                <p className="text-xs text-white/30 mt-1.5">Already billing annually — no further discount to offer.</p>
              )}
            </div>

            {/* Stablecoin */}
            <div>
              <label className={labelClass}>Payment Token</label>
              <div className="grid grid-cols-3 gap-2">
                {STABLECOINS.map(coin => (
                  <button
                    key={coin.symbol}
                    onClick={() => setFormData({ ...formData, token: coin.symbol })}
                    className={toggleBtn(formData.token === coin.symbol)}
                  >
                    <div className="text-lg mb-0.5">{coin.icon}</div>
                    <div>{coin.symbol}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Network */}
            <div>
              <label className={labelClass}>Blockchain Network</label>
              <div className="grid grid-cols-2 gap-2">
                {SUPPORTED_NETWORKS.map(net => (
                  <button
                    key={net.id}
                    onClick={() => setFormData({ ...formData, network: net.id })}
                    className={toggleBtn(formData.network === net.id) + " text-left"}
                  >
                    <span className="mr-1.5">{net.icon}</span>
                    <span>{net.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Renewal Reminder */}
            <div>
              <label className={labelClass}>Renewal Reminder (days before)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={formData.renewalReminder}
                onChange={(e) => setFormData({ ...formData, renewalReminder: e.target.value })}
                placeholder="3"
                className={inputClass}
              />
              <p className="text-xs text-white/30 mt-1.5">Set to 0 to disable.</p>
            </div>

            {/* Merchant Wallet Address */}
            <div>
              <label className={labelClass}>Merchant Wallet Address</label>
              <div className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl font-mono text-sm text-white/50 select-all break-all">
                {formData.walletAddress || '—'}
              </div>
              <p className="text-xs text-white/30 mt-1.5">Payments will be sent to your connected wallet.</p>
            </div>

            {/* Embed Code */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-white/35 font-medium">Embed Code</p>
                <button
                  onClick={handleCopy}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    copied
                      ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400'
                      : 'bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
              <div className="bg-black/40 border border-white/[0.06] rounded-xl p-4 overflow-x-auto">
                <pre className="text-xs text-emerald-400 font-mono whitespace-pre-wrap break-all">{generatedCode}</pre>
              </div>
              <p className="text-xs text-white/30 mt-2">Paste this anywhere in your website's HTML.</p>
            </div>
          </div>

          {/* Right — Live Preview */}
          <div className="px-6 py-5 space-y-4">
            <p className="text-[10px] uppercase tracking-widest text-white/35 font-medium">Live Preview</p>
            <p className="text-xs text-white/30">Exactly what your customers will see.</p>
            <div className="rounded-xl overflow-hidden border border-white/[0.08]" style={{ height: 680 }}>
              <iframe
                key={iframeSrc}
                src={iframeSrc}
                title="Payment portal preview"
                allow="clipboard-write"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default WidgetGenerator;
